import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BookOpen, ChevronsDownUp, ChevronsUpDown, FileDown } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Button } from '@/components/Shared/Button';
import { Card } from '@/components/Shared/Card';
import { Loading } from '@/components/Shared/Loading';
import { EmptyState } from '@/components/Shared/EmptyState';
import { useVehicleStore } from '@/store/useVehicleStore';
import {
  useVehicleLogbookStore,
  type VehicleLogbookEntryDto,
  type VehicleLogbookFilter,
} from '@/store/useVehicleLogbookStore';
import LogbookEntryForm from '@/pages/VehicleLogbook/LogbookEntryForm';
import VehicleLogbookSection from '@/pages/VehicleLogbook/VehicleLogbookSection';
import { formatDate, isoDate, parseIsoDate } from '@/utils/dateUtils';
import { formatKm } from '@/utils/distance';

const FILTER_INPUT_CLASS =
  'w-full px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 tabular-nums focus:outline-none focus:ring-2 focus:ring-[#00A871]/30';
const FILTER_LABEL_CLASS = 'block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1';

/** The API accepts at most ten years, both ends included (GetVehicleLogbookWorkflow.MaxPeriodDays). */
const MAX_PERIOD_DAYS = 3653;

/** Days from `from` to `to` (yyyy-MM-dd), both included. */
function periodDays(from: string, to: string): number {
  const utc = (value: string) => {
    const [year = 0, month = 1, day = 1] = value.split('-').map(Number);
    return Date.UTC(year, month - 1, day);
  };
  return (utc(to) - utc(from)) / 86_400_000 + 1;
}

/** Current calendar month as the default period. */
function currentMonth(): { from: string; to: string } {
  const now = new Date();
  return {
    from: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: isoDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
}

/**
 * Fahrtenbuch (FAFD-26): trips per vehicle and period, written while drivers stamp. The page
 * loads an overview per vehicle and each open vehicle's entries page by page; the PDF exports
 * the whole filter. The administration corrects entries with a signed history.
 */
export default function VehicleLogbookPage() {
  const { t } = useTranslation(['vehicles', 'common']);
  const [searchParams] = useSearchParams();
  const { vehicles, fetchVehicles } = useVehicleStore();
  const { overview, loading, saving, downloading, error, fetchOverview, updateEntry, downloadPdf } =
    useVehicleLogbookStore();

  const [filter, setFilter] = useState<VehicleLogbookFilter>(() => ({
    vehicleId: searchParams.get('vehicleId') ?? '',
    ...currentMonth(),
  }));
  const [showPassengers, setShowPassengers] = useState(false);
  const [editing, setEditing] = useState<VehicleLogbookEntryDto | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const periodValid =
    filter.from !== '' &&
    filter.to !== '' &&
    filter.from <= filter.to &&
    periodDays(filter.from, filter.to) <= MAX_PERIOD_DAYS;

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  useEffect(() => {
    if (periodValid) fetchOverview(filter);
  }, [filter, periodValid, fetchOverview]);

  const vehicleOptions = useMemo(
    () => [...vehicles].sort((a, b) => Number(b.isActive) - Number(a.isActive)),
    [vehicles],
  );

  const selectedVehicle = vehicles.find((v) => v.id === filter.vehicleId);
  const exportVehicleLabel = selectedVehicle
    ? (selectedVehicle.licensePlate ?? '')
    : t('vehicles:logbook.filters.allVehicles');

  const sections = (overview?.vehicles ?? []).filter((s) => (s.entryCount ?? 0) > 0);
  const collapsible = sections.length > 1;
  const allCollapsed = collapsible && sections.every((s) => collapsed.has(s.vehicle?.id ?? ''));

  const toggle = (vehicleId: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(vehicleId)) next.delete(vehicleId);
      else next.add(vehicleId);
      return next;
    });

  const toggleAll = () =>
    setCollapsed(allCollapsed ? new Set() : new Set(sections.map((s) => s.vehicle?.id ?? '')));

  const renderContent = () => {
    if (!periodValid) {
      return <EmptyState variant="error" title={t('vehicles:logbook.invalidPeriod')} />;
    }
    if (loading && !overview) {
      return <Loading />;
    }
    if (error) {
      return <EmptyState variant="error" title={t(error)} onRetry={() => fetchOverview(filter)} />;
    }
    if (sections.length === 0) {
      return (
        <EmptyState
          icon={<BookOpen className="w-12 h-12" strokeWidth={1.75} />}
          title={t('vehicles:logbook.empty')}
          description={t('vehicles:logbook.emptyHint')}
        />
      );
    }
    return (
      <div className="space-y-4 sm:space-y-6">
        {sections.map((section) => {
          const vehicleId = section.vehicle?.id ?? '';
          return (
            <VehicleLogbookSection
              key={vehicleId}
              section={section}
              filter={filter}
              open={!collapsed.has(vehicleId)}
              onToggle={collapsible ? () => toggle(vehicleId) : undefined}
              showPassengers={showPassengers}
              onEdit={setEditing}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('vehicles:logbook.title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">{t('vehicles:logbook.subtitle')}</p>
          </div>
          <div className="flex flex-col items-stretch sm:items-end gap-1">
            <Button
              variant="secondary"
              onClick={() => downloadPdf(filter, showPassengers)}
              disabled={downloading || !periodValid}
              loading={downloading}
              leftIcon={<FileDown className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('vehicles:logbook.exportPdf')}
            </Button>
            {/* What the export will contain — it follows the filter below. */}
            {periodValid && (
              <p className="text-[11px] text-slate-500 tabular-nums sm:text-right">
                {t('vehicles:logbook.exportHint', {
                  vehicle: exportVehicleLabel,
                  from: formatDate(parseIsoDate(filter.from)),
                  to: formatDate(parseIsoDate(filter.to)),
                  passengers: showPassengers
                    ? t('vehicles:logbook.exportWithPassengers')
                    : t('vehicles:logbook.exportWithoutPassengers'),
                })}
              </p>
            )}
          </div>
        </div>
      </div>

      <Card>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className={FILTER_LABEL_CLASS} htmlFor="logbook-vehicle">
              {t('vehicles:logbook.filters.vehicle')}
            </label>
            <select
              id="logbook-vehicle"
              value={filter.vehicleId}
              onChange={(e) => setFilter((f) => ({ ...f, vehicleId: e.target.value }))}
              className={FILTER_INPUT_CLASS}
            >
              <option value="">{t('vehicles:logbook.filters.allVehicles')}</option>
              {vehicleOptions.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.description ? `${v.licensePlate} · ${v.description}` : v.licensePlate}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={FILTER_LABEL_CLASS} htmlFor="logbook-from">
              {t('vehicles:logbook.filters.from')}
            </label>
            <input
              id="logbook-from"
              type="date"
              value={filter.from}
              onChange={(e) => setFilter((f) => ({ ...f, from: e.target.value }))}
              className={FILTER_INPUT_CLASS}
            />
          </div>
          <div>
            <label className={FILTER_LABEL_CLASS} htmlFor="logbook-to">
              {t('vehicles:logbook.filters.to')}
            </label>
            <input
              id="logbook-to"
              type="date"
              value={filter.to}
              onChange={(e) => setFilter((f) => ({ ...f, to: e.target.value }))}
              className={FILTER_INPUT_CLASS}
            />
          </div>
        </div>
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-[12px] text-slate-600">
              <input
                type="checkbox"
                checked={showPassengers}
                onChange={(e) => setShowPassengers(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
              />
              {t('vehicles:logbook.filters.showPassengers')}
            </label>
            {collapsible && (
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleAll}
                leftIcon={
                  allCollapsed ? (
                    <ChevronsUpDown className="w-4 h-4" strokeWidth={2} />
                  ) : (
                    <ChevronsDownUp className="w-4 h-4" strokeWidth={2} />
                  )
                }
              >
                {allCollapsed ? t('vehicles:logbook.expandAll') : t('vehicles:logbook.collapseAll')}
              </Button>
            )}
          </div>
          {periodValid && overview && (
            <div className="text-[13px] text-slate-600">
              {t('vehicles:logbook.totalLabel')}{' '}
              <span className="font-mono text-[18px] font-semibold tabular-nums text-slate-900">
                {formatKm(overview.totalKm)}
              </span>
              {overview.showPurposeKind && (
                <span className="ml-2 text-[12px] text-slate-500 tabular-nums">
                  {t('vehicles:logbook.kindSplit', { business: formatKm(overview.businessKm ?? 0), private: formatKm(overview.privateKm ?? 0) })}
                </span>
              )}
            </div>
          )}
        </div>
      </Card>

      {renderContent()}

      <LogbookEntryForm
        entry={editing}
        saving={saving}
        onClose={() => setEditing(null)}
        onSave={(data) => (editing ? updateEntry(editing, data, filter) : Promise.resolve(false))}
      />
    </div>
  );
}
